import React, { useState, useEffect } from 'react';
import {
	Card,
	CardBody,
	CardHeader,
} from '../../../../_metronic/_partials/controls';
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Checkbox
} from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import {
	deleteRoutinePlanType,
	getRoutinePlanTypeById,
	postRoutinePlanType,
	updateRoutinePlanType,
} from '../../../../api/routinePlanType';
import { useSkeleton } from '../../../hooks/useSkeleton';
import { alertError, alertSuccess } from '../../../../utils/logger';
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog';
import { shallowEqual, useSelector } from 'react-redux';
import MultilanguageTabBlock from '../../../components/MultilanguageTabBlock';
import { checkIsEmpty } from '../../../../utils/helpers';

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: '#F64E60',
		},
	},
});

function getEmptyRoutinePlanType() {
	return {
		fullName: {},
		active: true
	};
}

export default function EditRoutinePlanTypesPage() {
	const [routinePlanType, setRoutinePlanType] = useState(getEmptyRoutinePlanType());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const routinePlanTypeId = useParams().id;
	const history = useHistory();
	const user = useSelector((store) => store.authentication?.user, shallowEqual)

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!routinePlanTypeId) {
			disableLoadingData();
			return;
		}
		getRoutinePlanTypeById(routinePlanTypeId)
			.then((res) => {
				if (res.status === 200) {
					setRoutinePlanType(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({ error: error, customMessage: 'Could not get routine plan type.' });
				history.push('/routine-plan-types');
			});
	}, [routinePlanTypeId, disableLoadingData, history]);

	function saveRoutinePlanType() {
		if (checkIsEmpty(routinePlanType.fullName)) return alertError({ error: null, customMessage: 'The name is required in at least one of the languages.' })
		if (!routinePlanTypeId) {
			postRoutinePlanType(routinePlanType)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: 'Saved!',
							customMessage: 'Routine plan type successfully created.',
						});
						history.push('/routine-plan-types');
					}
				})
				.catch((error) => {
					alertError({ error: error, customMessage: 'Could not save routine plan type.' });
				});
		} else {
			updateRoutinePlanType(routinePlanTypeId, routinePlanType)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: 'Saved!',
							customMessage: 'Changes successfully saved.',
						});
						history.push('/routine-plan-types');
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: 'Could not save changes.',
					});
				});
		}
	}

	const handleChange = (element, lang) => (event) => {
		if (event.target.value === ' ') return
		if (lang) {
			if (!routinePlanType[element]) routinePlanType[element] = {}
			let newText = routinePlanType[element]
			newText[lang] = event.target.value
			setRoutinePlanType({ ...routinePlanType, [element]: newText })
		} else setRoutinePlanType({ ...routinePlanType, [element]: event.target.value });
	};

	const renderMultilanguageTabContent = (lang) => {
		return <>
			<br/>
			<TextField
				id={`fullName`}
				label='Full name'
				value={(routinePlanType.fullName && routinePlanType.fullName[lang]) || ''}
				onChange={handleChange('fullName', lang)}
				InputLabelProps={{
					shrink: true,
				}}
				margin='normal'
				variant='outlined'
				required
			/>
		</>
	}

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title='Edit routine plan type'></CardHeader>
					<CardBody>
						<MultilanguageTabBlock
							multilanguageTabContent = {renderMultilanguageTabContent}
						/>
						<br/>
						<FormControlLabel
							control={<Checkbox checked={routinePlanType.active} onChange={() => setRoutinePlanType({ ...routinePlanType, active: !routinePlanType.active })} name="checkActive" />}
							label="Active"
						/>
					</CardBody>
				</Card>
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<Button
						onClick={() => history.push('/routine-plan-types')}
						variant='outlined'
						style={{ marginRight: '20px' }}>
						Back
					</Button>
					<Button
						onClick={() => saveRoutinePlanType()}
						variant='outlined'
						color='primary'
						style={{ marginRight: '20px' }}>
						Save routine plan type
					</Button>
					{routinePlanTypeId && user?.role.includes('admin') && (
						<>
							<MuiThemeProvider theme={theme}>
								<Button
									onClick={() => setOpenConfirmDialog(true)}
									variant='outlined'
									color='secondary'
									style={{ marginRight: '20px' }}>
									Delete routine plan type
								</Button>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										marginLeft: 'auto',
									}}></div>
							</MuiThemeProvider>

							<ConfirmDialog
								title={'Are you sure you want to delete this routine plan type?'}
								open={openConfirmDialog}
								setOpen={setOpenConfirmDialog}
								onConfirm={() => {
									deleteRoutinePlanType(routinePlanTypeId)
										.then((res) => {
											if (res.status === 204 || res.status === 200) {
												alertSuccess({
													title: 'Deleted!',
													customMessage: 'Routine plan type deleted successfully',
												});
												history.push('/routine-plan-types');
											}
										})
										.catch((error) => {
											alertError({
												error: error,
												customMessage: 'Could not delete routine plan type.',
											});
										});
								}}
							/>
						</>
					)}
				</div>
			</>
		);
}
